import { apiFetch } from './client';

export type ToolSessionStatus = 'running' | 'finished' | 'unknown';
export type ToolParityStatus = 'OK' | 'DRIFT' | 'CRITICAL' | string;

export interface SnapshotOpenTrade {
  side?: string;
  quantity?: number;
  entryPrice?: number;
  stopLoss?: number;
  takeProfit?: number;
  orderId?: string;
  entryTime?: string;
}

export interface SessionSnapshot {
  sessionId: string;
  symbol: string | null;
  accountName: string | null;
  operatorId: string | null;
  status: ToolSessionStatus;
  startTime: string | null;
  endTime: string | null;
  totalPnL: number | null;
  totalBtPnL: number | null;
  tradeCount: number;
  openPositions: number;
  parityStatus: ToolParityStatus | null;
  warmupParityStatus: ToolParityStatus | null;
  config: Record<string, unknown>;
  openTrades: SnapshotOpenTrade[];
}

export interface ConfigSnapshot {
  sessionId: string;
  symbol: string | null;
  accountName: string | null;
  operatorId: string | null;
  dailyStartTime: string | null;
  dailyEndTime: string | null;
  config: Record<string, unknown>;
}

export interface SlippageAuditTrade {
  entryTime?: string;
  side?: string;
  quantity?: number;
  realPnL?: number | null;
  btPnL?: number | null;
  diff?: number | null;
  entryPrice?: number | null;
  entryTriggerPrice?: number | null;
  exitPrice?: number | null;
  closeReason?: string | null;
}

export interface SlippageAudit {
  sessionId: string;
  totalRealPnL: number | null;
  totalBtPnL: number | null;
  pnlDiff: number | null;
  tradeCount: number;
  comparableTradeCount: number;
  largestDiffs: SlippageAuditTrade[];
  warnings: string[];
}

export interface CompareSide {
  sessionId: string;
  symbol: string | null;
  accountName: string | null;
  operatorId: string | null;
  status: ToolSessionStatus;
  totalPnL: number | null;
  totalBtPnL: number | null;
  tradeCount: number;
  openPositions: number;
  parityStatus: ToolParityStatus | null;
  warmupParityStatus: ToolParityStatus | null;
}

export type ConfigDiffStatus = 'same' | 'changed' | 'missing_base' | 'missing_other';

export interface ConfigDiff {
  key: string;
  base: unknown;
  other: unknown;
  status: ConfigDiffStatus;
}

export interface CompareRenkoContext {
  index: number;
  direction: 'up' | 'down' | null;
  time: string | null;
  startTime: string | null;
  close: number | null;
  boxSize: number | null;
  candleAtr: number | null;
}

export interface CompareTrade {
  tradeId: string | null;
  orderId: string | null;
  side: 'long' | 'short' | null;
  status: 'open' | 'closed' | null;
  entryTime: string | null;
  exitTime: string | null;
  fillTime: string | null;
  entryPrice: number | null;
  exitPrice: number | null;
  entryTriggerPrice: number | null;
  exitTriggerPrice: number | null;
  quantity: number | null;
  contracts: number | null;
  pnl: number;
  btPnl: number;
  stopLoss: number | null;
  takeProfit: number | null;
  closeReason: string | null;
  valuePerPoint: number | null;
  account: string | null;
  outcome: string | null;
  entrySlippage: number | null;
  exitSlippage: number | null;
  entryRenko: CompareRenkoContext | null;
  exitRenko: CompareRenkoContext | null;
}

export type ComparePairStatus = 'matched' | 'matched_by_index' | 'base_only' | 'other_only';

export interface CompareTradePair {
  index: number;
  pairStatus: ComparePairStatus;
  base: CompareTrade | null;
  other: CompareTrade | null;
  pnlDiff: number | null;
  btPnlDiff: number | null;
  entryTimeDiffMs: number | null;
  entryRenkoIndexDiff: number | null;
  exitRenkoIndexDiff: number | null;
  sideMismatch: boolean;
  statusMismatch: boolean;
}

export interface SessionCompare {
  sessionId: string;
  otherSessionId: string;
  base: CompareSide;
  other: CompareSide;
  summaryDiff: {
    totalPnLDiff: number | null;
    totalBtPnLDiff: number | null;
    tradeCountDiff: number;
    openPositionsDiff: number;
  };
  configDiffs: ConfigDiff[];
  tradePairs: CompareTradePair[];
  pairing: {
    method: 'entry_time_tolerance';
    toleranceMs: number;
    matched: number;
    matchedByIndex: number;
    baseOnly: number;
    otherOnly: number;
  };
  warnings: string[];
}

export async function fetchSessionSnapshot(sessionId: string): Promise<SessionSnapshot> {
  return await apiFetch<SessionSnapshot>(
    `/api/tools/sessions/${encodeURIComponent(sessionId)}/snapshot`,
  );
}

export async function fetchSessionConfig(sessionId: string): Promise<ConfigSnapshot> {
  return await apiFetch<ConfigSnapshot>(
    `/api/tools/sessions/${encodeURIComponent(sessionId)}/config`,
  );
}

export async function fetchSlippageAudit(sessionId: string): Promise<SlippageAudit> {
  return await apiFetch<SlippageAudit>(
    `/api/tools/sessions/${encodeURIComponent(sessionId)}/slippage-audit`,
  );
}

export async function fetchSessionCompare(
  sessionId: string,
  otherSessionId: string,
): Promise<SessionCompare> {
  const qs = new URLSearchParams({ other: otherSessionId });
  return await apiFetch<SessionCompare>(
    `/api/tools/sessions/${encodeURIComponent(sessionId)}/compare?${qs.toString()}`,
  );
}
